/**
 * /me/performance — the employee's performance hub (ADR-0013 Phase 4).
 *
 * Three sections, each honoring the visibility gate (which is enforced
 * server-side — this UI never receives a manager's in-progress ratings):
 *   1. My goals — agree to proposed KRAs (goal_setting handshake).
 *   2. My self-assessment — rate own KRAs + competencies (review window).
 *   3. My reviews — the FINALIZED outcome only (scores + manager comments
 *      appear post-finalize), with the acknowledgment action (receipt, with an
 *      optional comment where disagreement is registered) and a client-side
 *      printable export (option 1 — no backend; documentId stays null).
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReviewResponseRatings } from '@kora/types';
import { meLayoutRoute } from './meLayout.js';
import {
  acknowledgeReview,
  agreeGoal,
  getMyAssignment,
  listMyAssignments,
  listMyGoals,
  listMyOutcomes,
  submitSelfAssessment,
  type AssignmentDetail,
  type PerfOutcome,
} from '../api/performance.js';
import { Alert } from '../components/Alert.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';
import { formatDate } from '../lib/format.js';

export const myPerformanceRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/performance',
  component: MyPerformancePage,
});

function MyPerformancePage(): JSX.Element {
  return (
    <section className="p-8 space-y-8 max-w-3xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">My performance</h1>
        <p className="text-sm text-gray-400">Agree your goals, complete your self-assessment, and view your finalized reviews.</p>
      </header>
      <MyGoals />
      <MySelfAssessments />
      <MyReviews />
    </section>
  );
}

// --- 1. Goals -------------------------------------------------------------

function MyGoals(): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['perf', 'my-goals'], queryFn: () => listMyGoals() });
  const agreeMut = useMutation({
    mutationFn: (id: string) => agreeGoal(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['perf', 'my-goals'] }); },
  });
  const goals = q.data?.goals ?? [];
  return (
    <Block title="My goals">
      {q.isLoading ? <Muted>Loading…</Muted> : goals.length === 0 ? <Muted>No goals assigned to you yet.</Muted> : (
        <div className="space-y-2">
          {goals.map((g) => (
            <div key={g.id} className="rounded border border-gray-900 p-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm text-white">{g.title}{g.weight != null ? <span className="text-gray-500 text-xs ml-2">{Number(g.weight)}%</span> : null}</p>
                <p className="text-xs text-gray-500 mt-0.5">{g.status}</p>
              </div>
              {g.status === 'draft' ? (
                <button type="button" onClick={() => agreeMut.mutate(g.id)} disabled={agreeMut.isPending} className={primaryBtn}>Agree</button>
              ) : <span className="text-[0.65rem] uppercase tracking-wide text-green-400">agreed</span>}
            </div>
          ))}
        </div>
      )}
      <ApiErrorNotice error={agreeMut.error} fallback="Could not agree goal." compact />
    </Block>
  );
}

// --- 2. Self-assessment ---------------------------------------------------

function MySelfAssessments(): JSX.Element {
  const q = useQuery({ queryKey: ['perf', 'my-assignments'], queryFn: () => listMyAssignments() });
  // My self assignments whose cycle is in the review window.
  const selfAssignments = (q.data?.assignments ?? []).filter((a) => a.type === 'self' && a.cycle.status === 'review');
  return (
    <Block title="My self-assessment">
      {q.isLoading ? <Muted>Loading…</Muted> : selfAssignments.length === 0 ? <Muted>Nothing to self-assess right now.</Muted> : (
        <div className="space-y-3">
          {selfAssignments.map((a) => <SelfAssessmentCard key={a.id} assignmentId={a.id} cycleName={a.cycle.name} submitted={!!a.response?.submittedAt} />)}
        </div>
      )}
    </Block>
  );
}

function SelfAssessmentCard({ assignmentId, cycleName, submitted }: { assignmentId: string; cycleName: string; submitted: boolean }): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(!submitted);
  const detailQ = useQuery({ queryKey: ['perf', 'assignment', assignmentId], queryFn: () => getMyAssignment(assignmentId), enabled: editing });

  return (
    <div className="rounded border border-gray-900 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-white">{cycleName}</p>
        {submitted ? <span className="text-[0.65rem] uppercase tracking-wide text-green-400">submitted</span> : <span className="text-[0.65rem] uppercase tracking-wide text-gray-500">awaiting you</span>}
      </div>
      {!editing ? (
        <button type="button" onClick={() => setEditing(true)} className={secondaryBtn}>{submitted ? 'View / update' : 'Start'}</button>
      ) : detailQ.isLoading ? <Muted>Loading…</Muted> : detailQ.isError || !detailQ.data ? <Alert variant="error" compact>Could not load.</Alert> : (
        <SelfForm assignmentId={assignmentId} detail={detailQ.data} onDone={() => { setEditing(false); void qc.invalidateQueries({ queryKey: ['perf', 'my-assignments'] }); void qc.invalidateQueries({ queryKey: ['perf', 'assignment', assignmentId] }); }} />
      )}
    </div>
  );
}

function SelfForm({ assignmentId, detail, onDone }: { assignmentId: string; detail: AssignmentDetail; onDone: () => void }): JSX.Element {
  const existing = detail.ratings;
  const [scores, setScores] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const r of existing?.kraRatings ?? []) init[`kra:${r.ref}`] = String(r.score);
    for (const r of existing?.competencyRatings ?? []) init[`comp:${r.ref}`] = String(r.score);
    return init;
  });
  const [comments, setComments] = useState(existing?.comments ?? '');
  const min = detail.template?.ratingScale.min ?? 1;
  const max = detail.template?.ratingScale.max ?? 5;

  const mut = useMutation({
    mutationFn: () => {
      const ratings: ReviewResponseRatings = {
        kraRatings: detail.kras.map((k) => ({ ref: k.id, score: Number(scores[`kra:${k.id}`] ?? 0) })),
        competencyRatings: (detail.template?.competencies ?? []).map((c) => ({ ref: c.key, score: Number(scores[`comp:${c.key}`] ?? 0) })),
        ...(comments ? { comments } : {}),
      };
      return submitSelfAssessment(assignmentId, ratings);
    },
    onSuccess: onDone,
  });
  const set = (k: string, v: string): void => setScores((s) => ({ ...s, [k]: v }));

  return (
    <form onSubmit={(e) => { e.preventDefault(); mut.mutate(); }} className="space-y-3">
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-gray-500">KRAs</p>
        {detail.kras.map((k) => (
          <Row key={k.id} label={k.title} sub={k.weight != null ? `${Number(k.weight)}%` : undefined}>
            <input type="number" min={min} max={max} value={scores[`kra:${k.id}`] ?? ''} onChange={(e) => set(`kra:${k.id}`, e.target.value)} aria-label={`${k.title} rating`} className={`${inputCls} w-20`} />
          </Row>
        ))}
      </div>
      {detail.template ? (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-gray-500">Competencies</p>
          {detail.template.competencies.map((c) => (
            <Row key={c.key} label={c.label}>
              <input type="number" min={min} max={max} value={scores[`comp:${c.key}`] ?? ''} onChange={(e) => set(`comp:${c.key}`, e.target.value)} aria-label={`${c.label} rating`} className={`${inputCls} w-20`} />
            </Row>
          ))}
        </div>
      ) : null}
      <label className="block space-y-1">
        <span className="text-xs text-gray-400">Comments</span>
        <textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3} className={`${inputCls} w-full`} />
      </label>
      <button type="submit" disabled={mut.isPending} className={primaryBtn}>{mut.isPending ? 'Submitting…' : 'Submit self-assessment'}</button>
      <ApiErrorNotice error={mut.error} fallback="Could not submit." compact />
    </form>
  );
}

// --- 3. Finalized reviews (the gate + acknowledgment + export) -------------

function MyReviews(): JSX.Element {
  const q = useQuery({ queryKey: ['perf', 'my-outcomes'], queryFn: () => listMyOutcomes() });
  const outcomes = q.data?.outcomes ?? [];
  return (
    <Block title="My reviews">
      {q.isLoading ? <Muted>Loading…</Muted> : outcomes.length === 0 ? <Muted>No finalized reviews yet. Your manager's ratings are not visible until your review is finalized.</Muted> : (
        <div className="space-y-3">
          {outcomes.map((o) => <ReviewCard key={o.id} outcome={o} />)}
        </div>
      )}
    </Block>
  );
}

function ReviewCard({ outcome }: { outcome: PerfOutcome }): JSX.Element {
  const qc = useQueryClient();
  const [comment, setComment] = useState('');
  const ackMut = useMutation({
    mutationFn: () => acknowledgeReview(outcome.id, comment || undefined),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['perf', 'my-outcomes'] }); },
  });

  return (
    <div className="rounded border border-gray-900 p-4 space-y-3 print:border-black print:text-black">
      <div className="flex items-center justify-between">
        <p className="text-sm text-white print:text-black">{outcome.cycle?.name ?? outcome.cycleId}</p>
        <AckBadge status={outcome.acknowledgmentStatus} />
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <Score label="KRA" value={outcome.kraScore} />
        <Score label="Competency" value={outcome.competencyScore} />
        <Score label="Final" value={outcome.finalScore} emphasis />
      </div>
      {outcome.recommendation ? <p className="text-xs text-gray-300 print:text-black">Recommendation: {outcome.recommendation}</p> : null}
      <p className="text-xs text-gray-500">Finalized {formatDate(outcome.finalizedAt)}</p>

      <div className="flex gap-2 print:hidden">
        <button type="button" onClick={() => window.print()} className={secondaryBtn}>Print / export</button>
      </div>

      {outcome.acknowledgmentStatus === 'awaiting' ? (
        <div className="space-y-2 border-t border-gray-900 pt-3 print:hidden">
          <p className="text-xs text-gray-400">Acknowledge that you have seen and discussed this review. This is a receipt — it does not mean you agree. You may add a comment (e.g. to register disagreement).</p>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Optional comment" className={`${inputCls} w-full`} />
          <button type="button" onClick={() => ackMut.mutate()} disabled={ackMut.isPending} className={primaryBtn}>{ackMut.isPending ? 'Submitting…' : 'Acknowledge receipt'}</button>
          <ApiErrorNotice error={ackMut.error} fallback="Could not acknowledge." compact />
        </div>
      ) : outcome.acknowledgmentStatus === 'acknowledged' ? (
        <p className="text-xs text-green-400 print:text-black">Acknowledged{outcome.acknowledgmentComment ? ` — "${outcome.acknowledgmentComment}"` : ''}.</p>
      ) : (
        <p className="text-xs text-gray-400 print:text-black">Acknowledgment waived by HR{outcome.acknowledgmentWaiveReason ? ` — ${outcome.acknowledgmentWaiveReason}` : ''}.</p>
      )}
    </div>
  );
}

// --- shared ---------------------------------------------------------------

export function AckBadge({ status }: { status: 'acknowledged' | 'awaiting' | 'waived' }): JSX.Element {
  const cls = status === 'acknowledged' ? 'text-green-400' : status === 'waived' ? 'text-gray-400' : 'text-yellow-300';
  return <span className={`text-[0.65rem] uppercase tracking-wide ${cls}`}>{status}</span>;
}

function Score({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }): JSX.Element {
  return (
    <div className="rounded border border-gray-900 p-2 print:border-black">
      <p className="text-[0.65rem] uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`${emphasis ? 'text-lg text-white' : 'text-sm text-gray-200'} print:text-black`}>{Number(value)}</p>
    </div>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="space-y-3">
      <h2 className="font-serif text-xl">{title}</h2>
      {children}
    </div>
  );
}
function Row({ label, sub, children }: { label: string; sub?: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-gray-900 p-2">
      <p className="text-sm text-white truncate">{label}{sub ? <span className="text-gray-500 text-xs ml-2">{sub}</span> : null}</p>
      {children}
    </div>
  );
}
function Muted({ children }: { children: React.ReactNode }): JSX.Element {
  return <p className="text-sm text-gray-500">{children}</p>;
}

const inputCls = 'rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm';
const primaryBtn = 'rounded bg-green-700 text-white text-xs px-3 py-1.5 disabled:opacity-50';
const secondaryBtn = 'rounded border border-gray-700 text-xs px-3 py-1.5 hover:bg-gray-900';
